import { Container } from 'pixi.js';
import type { BoardTile } from '../../model/board/BoardTypes';
import { BoardTileWidget } from './BoardTile';
import { PlayerToken } from './PlayerToken';

export interface BoardWidgetOptions {
  tiles: readonly BoardTile[];
  /** Tile width/height in px. Default 80. */
  tileSize?: number;
  /** Pixels between adjacent tiles. Default 10. */
  tileSpacing?: number;
  /** Tiles per row before wrapping back the other way. Default 5. */
  tilesPerRow?: number;
  /** Pixel gap between rows in the snake layout. Default = tileSize * 1.4. */
  rowSpacing?: number;
  /** Theme accent + text colours forwarded to tile widgets. */
  accentColor?: number;
  textColor?: number;
  /** Player token visual options. */
  token?: {
    fillColor?: number;
    glyph?: string;
  };
}

/**
 * Snake-layout board: tiles flow left-to-right, then right-to-left on the
 * next row, then left-to-right again — keeps a long path readable on a
 * phone-shaped viewport without horizontal scrolling.
 *
 * <p>Owns the tile widgets and the {@link PlayerToken}. Subscribers to a
 * {@code BoardModel} call {@link setHighlightedIndex} after every
 * {@code position-changed} event; the host scene calls {@link tileCenter}
 * to drive {@link PlayerToken#hopTo} between tiles.
 */
export class BoardWidget extends Container {
  private readonly tiles: BoardTileWidget[] = [];
  private readonly token: PlayerToken;
  private readonly tileSize: number;
  private readonly tileSpacing: number;
  private readonly tilesPerRow: number;
  private readonly rowSpacing: number;
  private highlightedIndex = 0;

  constructor(opts: BoardWidgetOptions) {
    super();
    this.tileSize = opts.tileSize ?? 80;
    this.tileSpacing = opts.tileSpacing ?? 10;
    this.tilesPerRow = Math.max(2, opts.tilesPerRow ?? 5);
    this.rowSpacing = opts.rowSpacing ?? this.tileSize * 1.4;

    for (const data of opts.tiles) {
      const tile = new BoardTileWidget(data, {
        size: this.tileSize,
        accentColor: opts.accentColor,
        textColor: opts.textColor,
      });
      this.tiles.push(tile);
      this.addChild(tile);
    }
    this.layoutTiles();
    if (this.tiles[0]) this.tiles[0].setHighlighted(true);

    this.token = new PlayerToken({
      size: this.tileSize * 0.7,
      fillColor: opts.token?.fillColor ?? opts.accentColor,
      glyph: opts.token?.glyph,
    });
    const start = this.tileCenter(0);
    this.token.snapTo(start.x, start.y);
    this.addChild(this.token);
  }

  /** Total bounding-box width of the laid-out board (excludes margins). */
  totalWidth(): number {
    return this.tilesPerRow * this.tileSize + (this.tilesPerRow - 1) * this.tileSpacing;
  }

  /** Total bounding-box height of the laid-out board. */
  totalHeight(): number {
    const rows = Math.ceil(this.tiles.length / this.tilesPerRow);
    return rows * this.tileSize + (rows - 1) * (this.rowSpacing - this.tileSize);
  }

  /** Centre coords of the tile at {@code index}, in widget-local space. */
  tileCenter(index: number): { x: number; y: number } {
    const row = Math.floor(index / this.tilesPerRow);
    const colInRow = index % this.tilesPerRow;
    // Even rows go left-to-right; odd rows right-to-left (the snake).
    const visualCol = row % 2 === 0 ? colInRow : this.tilesPerRow - 1 - colInRow;
    const x = visualCol * (this.tileSize + this.tileSpacing) + this.tileSize / 2;
    const y = row * this.rowSpacing + this.tileSize / 2;
    return { x, y };
  }

  /** Move the highlight ring to a new tile. Old highlight clears. */
  setHighlightedIndex(index: number): void {
    if (index < 0 || index >= this.tiles.length) return;
    this.tiles[this.highlightedIndex]?.setHighlighted(false);
    this.highlightedIndex = index;
    this.tiles[index]?.setHighlighted(true);
  }

  /** Animate the token from its current spot to {@code tileIndex}.
   * Resolves when the hop animation completes. */
  hopTokenTo(tileIndex: number, durationMs: number = 280): Promise<void> {
    const target = this.tileCenter(tileIndex);
    return this.token.hopTo(target.x, target.y, durationMs);
  }

  destroyBoard(): void {
    this.token.destroyToken();
  }

  private layoutTiles(): void {
    for (let i = 0; i < this.tiles.length; i++) {
      const c = this.tileCenter(i);
      this.tiles[i]!.x = c.x;
      this.tiles[i]!.y = c.y;
    }
  }
}
